import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { isValidPhoneNumber } from 'libphonenumber-js';
import validator from 'validator';
import { SiteNav, MinimalFooter } from './shared';
import { trackMeta } from '../lib/metaTrack';
import { sendLead } from '../lib/emailLead';
import Seo from './Seo';

/* -------------------------------------------------------------------------
   AnfrageForm — two-step Erstanalyse request (replaces the Calendly link:
   booking a call is too high-commitment for colder traffic).

     Step 1 · Situation      — how the portfolio is managed, size, challenge
     Step 2 · Kontaktdaten   — who to reach + implicit privacy consent

   Real validation (validator.isEmail + libphonenumber-js isValidPhoneNumber).
   NOT wired: "Absenden" only logs the payload — hook to routers/forms.py later.
   Reachable at /anfrage; CTAs already point here.
   ------------------------------------------------------------------------- */

const TOTAL_STEPS = 2;

const VERWALTUNG = ['Selbstverwaltung', 'Klassische Hausverwaltung', 'Mischform', 'Andere Lösung'];
const HERAUSFORDERUNG = [
  'Profitabilität der Immobilie',
  'Zeitaufwand für die Immobilie',
  'Politische Entwicklungen und Einfluss auf die Immobilie',
];
const ANREDE = ['Herr', 'Frau'];

function Field({ label, required, error, children }) {
  return (
    <div className={`field ${error ? 'error' : ''}`}>
      <label className="field-label">
        {label}{required && <span style={{ color: 'var(--bordeaux)', marginLeft: 4 }}>*</span>}
      </label>
      {children}
      {error && <div className="field-error">{error}</div>}
    </div>
  );
}

function StepHeader({ eyebrow, title }) {
  return (
    <div style={{ marginBottom: 32 }}>
      <div className="eyebrow" style={{ color: 'var(--charcoal-muted)', marginBottom: 12 }}>{eyebrow}</div>
      <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(28px, 3.4vw, 40px)', lineHeight: 1.15, color: 'var(--anthracite)', margin: 0 }}>
        {title}
      </h1>
    </div>
  );
}

export default function AnfrageForm() {
  const [step, setStep] = useState(1);
  const [data, setData] = useState({
    verwaltung: '', einheiten: '', herausforderung: '',
    anrede: '', vorname: '', nachname: '', email: '', telefon: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  // Meta: SubmitApplication ("form started") fires once here — the CTAs route
  // to this page, so this covers both CTA clicks and direct visits.
  const startTracked = useRef(false);
  useEffect(() => {
    if (startTracked.current) return;
    startTracked.current = true;
    trackMeta('SubmitApplication', { customData: { content_name: 'Erstanalyse Anfrage' } });
  }, []);

  const validateStep = (s) => {
    const e = {};
    if (s === 1) {
      if (!data.verwaltung) e.verwaltung = 'Bitte auswählen.';
      if (!data.einheiten || parseInt(data.einheiten, 10) < 1) e.einheiten = 'Bitte eine gültige Anzahl angeben.';
      if (!data.herausforderung) e.herausforderung = 'Bitte auswählen.';
    } else {
      if (!data.vorname.trim()) e.vorname = 'Bitte ausfüllen.';
      if (!data.nachname.trim()) e.nachname = 'Bitte ausfüllen.';
      if (!validator.isEmail(data.email.trim())) e.email = 'Gültige E-Mail erforderlich.';
      if (!data.telefon || !isValidPhoneNumber(data.telefon)) e.telefon = 'Gültige Telefonnummer erforderlich.';
    }
    return e;
  };

  const next = () => {
    const e = validateStep(1);
    setErrors(e);
    if (!Object.keys(e).length) { setStep(2); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };
  const back = () => { setErrors({}); setStep(1); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const e = validateStep(2);
    setErrors(e);
    if (Object.keys(e).length) return;

    setStatus('sending');
    try {
      await sendLead({
        formType: 'Erstanalyse-Anfrage',
        replyTo: data.email.trim(),
        fields: {
          Verwaltung: data.verwaltung,
          Einheiten: data.einheiten,
          Herausforderung: data.herausforderung,
          Anrede: data.anrede,
          Vorname: data.vorname.trim(),
          Nachname: data.nachname.trim(),
          'E-Mail': data.email.trim(),
          Telefon: data.telefon,
        },
      });
      trackMeta('CompleteRegistration', {
        userData: { email: data.email.trim(), phone: data.telefon },
        customData: { content_name: 'Erstanalyse Anfrage' },
      });
      setStatus('sent');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[AnfrageForm] send failed:', err);
      setStatus('error');
    }
  };

  const einheitenSuffix = data.einheiten === '1' ? 'Einheit' : 'Einheiten';
  const pct = (step / TOTAL_STEPS) * 100;

  return (
    <div className="page-enter" data-screen-label="Anfrage (2-Schritt)">
      <Seo
        path="/anfrage"
        title="Erstanalyse anfragen — hamba"
        description="Fordern Sie in zwei Schritten Ihre kostenlose Erstanalyse an."
        noindex
      />
      <SiteNav />

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '96px var(--gutter) 80px' }}>
        {status === 'sent' ? (
          <div style={{ paddingTop: 20 }}>
            <div className="eyebrow" style={{ color: 'var(--bordeaux)', marginBottom: 14 }}>Vielen Dank</div>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: 'clamp(30px, 4vw, 44px)', color: 'var(--anthracite)', margin: '0 0 16px', lineHeight: 1.15 }}>
              Ihre Anfrage ist bei uns eingegangen.
            </h1>
            <p style={{ fontSize: 16, lineHeight: 1.7, color: 'var(--charcoal-muted)', maxWidth: '60ch' }}>
              Wir prüfen Ihre Angaben und melden uns zeitnah mit einer ersten Einschätzung Ihres Portfolios.
            </p>
            <Link to="/" style={{ display: 'inline-block', marginTop: 28, color: 'var(--bordeaux)', fontSize: 14 }}>← Zurück zur Startseite</Link>
          </div>
        ) : (
        <>
        <div style={{ marginBottom: 40 }}>
          <div className="eyebrow" style={{ color: 'var(--charcoal-muted)', marginBottom: 10 }}>
            Schritt {step} von {TOTAL_STEPS} · {step === 1 ? 'Situation' : 'Kontaktdaten'}
          </div>
          <div style={{ position: 'relative', height: 2, background: 'var(--cream-line)' }}>
            <div style={{ position: 'absolute', inset: 0, width: `${pct}%`, background: 'var(--bordeaux)', transition: 'width 320ms ease' }} />
          </div>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          {step === 1 && (
            <>
              <StepHeader eyebrow="Ihre Situation" title="Erzählen Sie uns von Ihrem Portfolio." />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
                <Field label="Wie wird Ihr Portfolio derzeit verwaltet?" required error={errors.verwaltung}>
                  <select value={data.verwaltung} onChange={(e) => set('verwaltung')(e.target.value)}>
                    <option value="">— Bitte auswählen</option>
                    {VERWALTUNG.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>

                <div className={`field number-field ${errors.einheiten ? 'error' : ''}`}>
                  <label className="field-label">
                    Wie viele Einheiten umfasst Ihr Portfolio?<span style={{ color: 'var(--bordeaux)', marginLeft: 4 }}>*</span>
                  </label>
                  <div className="number-wrap">
                    <input
                      type="text" inputMode="numeric" pattern="[0-9]*"
                      value={data.einheiten ? Number(data.einheiten).toLocaleString('de-DE') : ''}
                      onChange={(e) => set('einheiten')(e.target.value.replace(/\D/g, ''))}
                      placeholder="z. B. 12" aria-label="Anzahl Einheiten"
                    />
                    {data.einheiten && <span className="number-suffix">{einheitenSuffix}</span>}
                  </div>
                  {errors.einheiten && <div className="field-error">{errors.einheiten}</div>}
                </div>

                <Field label="Was ist derzeit Ihre größte Herausforderung mit Ihren Immobilien?" required error={errors.herausforderung}>
                  <select value={data.herausforderung} onChange={(e) => set('herausforderung')(e.target.value)}>
                    <option value="">— Bitte auswählen</option>
                    {HERAUSFORDERUNG.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </Field>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <StepHeader eyebrow="Kontaktdaten" title="Wohin dürfen wir uns melden?" />
              <div style={{ marginBottom: 28 }}>
                <label className="field-label" style={{ display: 'block', marginBottom: 12 }}>Anrede</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
                  {ANREDE.map((opt) => {
                    const active = data.anrede === opt;
                    return (
                      <button type="button" key={opt}
                        onClick={() => set('anrede')(opt)}
                        style={{
                          padding: '11px 16px', textAlign: 'left',
                          background: active ? 'color-mix(in oklab, var(--bordeaux) 7%, var(--cream))' : 'var(--cream)',
                          color: 'var(--anthracite)', fontFamily: 'var(--grotesk)', fontSize: 14.5,
                          border: `1px solid ${active ? 'var(--bordeaux)' : 'var(--cream-line)'}`,
                          borderRadius: 1, cursor: 'pointer',
                        }}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', columnGap: 28, rowGap: 24 }}>
                <Field label="Vorname" required error={errors.vorname}>
                  <input type="text" value={data.vorname} onChange={(e) => set('vorname')(e.target.value)} autoComplete="given-name" />
                </Field>
                <Field label="Nachname" required error={errors.nachname}>
                  <input type="text" value={data.nachname} onChange={(e) => set('nachname')(e.target.value)} autoComplete="family-name" />
                </Field>
                <Field label="E-Mail-Adresse" required error={errors.email}>
                  <input type="email" value={data.email} onChange={(e) => set('email')(e.target.value)} autoComplete="email" />
                </Field>
                <div className={`field phone-field ${errors.telefon ? 'error' : ''}`}>
                  <label className="field-label">Telefonnummer<span style={{ color: 'var(--bordeaux)', marginLeft: 4 }}>*</span></label>
                  <PhoneInput defaultCountry="de" value={data.telefon}
                    onChange={(phone) => set('telefon')(phone)}
                    inputProps={{ 'aria-label': 'Telefonnummer', autoComplete: 'tel' }} />
                  {errors.telefon && <div className="field-error">{errors.telefon}</div>}
                </div>
              </div>

              <p className="hero-leadform__privacy" style={{ marginTop: 28 }}>
                Mit dem Absenden bestätigen Sie, dass Sie unsere{' '}
                <Link to="/agb" target="_blank" rel="noopener noreferrer">AGB</Link>
                {' '}und{' '}
                <Link to="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzhinweise</Link>
                {' '}gelesen haben und der Verarbeitung Ihrer Angaben zur Bearbeitung Ihrer Anfrage zustimmen.
              </p>
            </>
          )}

          <div style={{ marginTop: 40, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {step > 1 ? (
              <button type="button" onClick={back}
                style={{ padding: '12px 22px', background: 'transparent', color: 'var(--anthracite)', border: '1px solid var(--anthracite)', fontFamily: 'var(--grotesk)', fontSize: 13, letterSpacing: '0.06em', borderRadius: 1, cursor: 'pointer' }}>
                <span aria-hidden="true">←</span> Zurück
              </button>
            ) : <span />}

            {step < TOTAL_STEPS ? (
              <button type="button" onClick={next}
                style={{ padding: '14px 28px', background: 'var(--bordeaux)', color: 'var(--cream)', fontFamily: 'var(--grotesk)', fontSize: 13, letterSpacing: '0.08em', fontWeight: 500, border: '1px solid var(--bordeaux)', borderRadius: 1, cursor: 'pointer' }}>
                Weiter <span aria-hidden="true">→</span>
              </button>
            ) : (
              <button type="submit" disabled={status === 'sending'}
                style={{ padding: '14px 28px', background: 'var(--bordeaux)', color: 'var(--cream)', fontFamily: 'var(--grotesk)', fontSize: 13, letterSpacing: '0.08em', fontWeight: 500, border: '1px solid var(--bordeaux)', borderRadius: 1, cursor: status === 'sending' ? 'wait' : 'pointer', opacity: status === 'sending' ? 0.7 : 1 }}>
                {status === 'sending' ? 'Wird gesendet…' : <>Erstanalyse anfragen <span aria-hidden="true">→</span></>}
              </button>
            )}
          </div>

          {status === 'error' && (
            <p role="alert" style={{ marginTop: 16, color: 'var(--bordeaux)', fontSize: 13, lineHeight: 1.55 }}>
              Ihre Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder schreiben Sie an{' '}
              <a href="mailto:frank.balz@hambacore.com" style={{ color: 'var(--bordeaux)' }}>frank.balz@hambacore.com</a>.
            </p>
          )}

          <p className="muted" style={{ fontSize: 12, marginTop: 18 }}>
            <span style={{ color: 'var(--bordeaux)' }}>*</span> Pflichtfeld
          </p>
        </form>
        </>
        )}
      </main>

      <MinimalFooter />
    </div>
  );
}
